// @ts-check
import { removeElement } from '../utils';

/**
 * @typedef {[number, number, number, number]} ViewBox [min-x, min-y, width, height]
 * @typedef {{ countryId: string, geographyId: string, title: string }} MarkerClickPayload
 * @typedef {{ countryId: string, geographyId: string, name: string, x: number, y: number, countryIsoCode?: string, tooltipPosition?: string }} TermModel
 * @typedef {{ countryId: string, countryIsoCode: string, countryName: string, countryPosition: { x: number, y: number }, countryViewBox: ViewBox, terms: TermModel[] }} CountryTermModel
 */

/**
 * @param {number} t
 * @returns {number}
 */
function easeInOut(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // Ease-in-out function
}

/**
 * @param {HTMLDivElement} marker
 * @param {{ x: number, y: number }} positionOnSvg
 * @param {SVGElement} svgElement
 * @param {HTMLElement} mainElement
 */
function setMarkerPosition(marker, positionOnSvg, svgElement, mainElement) {
  // Add point to svg. We are using it to calculate the markers position
  const point = /** @type{SVGCircleElement}*/ (document.createElementNS('http://www.w3.org/2000/svg', 'circle'));
  point.setAttribute('cx', positionOnSvg.x.toString());
  point.setAttribute('cy', positionOnSvg.y.toString());
  point.setAttribute('r', '0.00001'); // Should be a small value, otherwise it does not calculate in firefox

  svgElement.appendChild(point);

  const pointRect = point.getBoundingClientRect();
  const mainRect = mainElement.getBoundingClientRect();

  const left = pointRect.left - mainRect.left - (marker.offsetWidth / 2);
  let top = pointRect.top - mainRect.top - (marker.offsetHeight / 2);

  marker.style.left = `${left}px`;
  marker.style.top = `${top}px`;

  // Remove the point again, it is not used anymore.
  removeElement(point);
}

/**
 * Sort markers from top left to bottom right
 * @param {HTMLDivElement[]} markers markers to be sorted
 * @returns {HTMLDivElement[]} Sorted markers
 */
function sortMarkers(markers) {
  return markers.sort((a, b) => {
    const tolerance = 10;
    // Sammenlign y-koordinaterne med tolerance
    const yDiff = Math.abs(a.offsetTop - b.offsetTop);
    if (yDiff > tolerance) {
      return a.offsetTop - b.offsetTop; // Sortér fra top til bund
    }

    // Hvis y-koordinaterne er ens, sammenlign x-koordinaterne
    return a.offsetLeft - b.offsetLeft; // Sortér fra venstre til højre
  });
}

/**
 * Gets size of a pseudo element (:after)
 * @param {HTMLDivElement} marker
 * @param {number} [padding]
 * @return {{ left: number, top: number, right: number, bottom: number }}
 */
function getPseudoElementSize(marker, padding = 0) {
  const markerStyle = window.getComputedStyle(marker);
  const afterStyle = window.getComputedStyle(marker, ':after');

  const width = parseFloat(afterStyle.width);
  const height = parseFloat(afterStyle.height);
  const left = parseFloat(afterStyle.left) + parseFloat(markerStyle.left);
  const top = parseFloat(afterStyle.top) + parseFloat(markerStyle.top);

  return {
    left: left - padding,
    top: top - padding,
    right: left + width + padding,
    bottom: top + height + padding,
  };
}

/**
 * @param {HTMLDivElement} marker
 * @return {{ left: number, top: number, right: number, bottom: number }}
 */
function getElementSize(marker) {
  const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = marker; // Get size of marker2' mark/dot
  return {
    left: offsetLeft,
    top: offsetTop,
    right: offsetLeft + offsetWidth,
    bottom: offsetTop + offsetHeight,
  };
}

/**
 * @param {{ left: number, top: number, right: number, bottom: number }} d1
 * @param {{ left: number, top: number, right: number, bottom: number }} d2
 * @returns {boolean} True if collides
 */
const hasCollision = (d1, d2) => {
  const horizontal = d1.left < d2.right && d1.right > d2.left;
  const vertical = d1.top < d2.bottom && d1.bottom > d2.top;
  return horizontal && vertical;
};

/**
 * Check if a point1' tooltip collides with point2' tooltip and marker
 * @param {HTMLDivElement} marker1 Marker consists of a dot and a tooltip
 * @param {HTMLDivElement} marker2 Marker consists of a dot and a tooltip
 * @param {boolean} doMarkerCheck Set to true to check if markers hit each others
 * @returns {{ collision: boolean, distance: number }}
 */
function checkCollision(marker1, marker2, doMarkerCheck) {
  const rect1 = getPseudoElementSize(marker1, 3);
  const rect2 = getPseudoElementSize(marker2);

  const dot1 = getElementSize(marker1); // Get size of marker1' mark/dot
  const dot2 = getElementSize(marker2); // Get size of marker2' mark/dot

  const tooltipsCollision = hasCollision(rect1, rect2); // Check collision between the 2 tooltips
  const tooltip1Collision = hasCollision(rect1, dot2); // Check if tooltip1 hits dot2
  const dotsCollision = hasCollision(dot1, dot2); // Maybe some tolerance???? Eller skal den kigge på cirkel i stedet for firkant, not det er dot dot, se screenshot på anden skærm.

  // If marker1' dot collides with marker2' dot, then do not check if marker1' tooltip hits marker2' dot
  doMarkerCheck = !dotsCollision;

  const collision = doMarkerCheck ? tooltipsCollision || tooltip1Collision : tooltipsCollision;

  return {
    collision,
    distance: calculateDistance(rect1, dot2),
  };
}

/**
 * Calculates distance to another element.
 * @param {{ left: number, top: number, right: number, bottom: number }} rect1
 * @param {{ left: number, top: number, right: number, bottom: number }} dot2
 * @returns {number}
 */
function calculateDistance(rect1, dot2) {
  const horizontalD = Math.max(0, Math.max(rect1.left, dot2.left) - Math.min(rect1.right, dot2.right));
  const verticalD = Math.max(0, Math.max(rect1.top, dot2.top) - Math.min(rect1.bottom, dot2.bottom));
  const distanceDot = Math.sqrt(horizontalD ** 2 + verticalD ** 2); // ** = eksponentoperatoren: 73 ** 2 Dette er 73 opløftet i anden potens, hvilket er 5329
  return distanceDot;
}

/**
 * Tries to find a good position for tooltip on the marker
 * @param {HTMLDivElement[]} markers1
 * @param {HTMLDivElement[]} markers2
 * @param {boolean} doMarkerCheck Set to true to check if markers hit each others
 * @param {boolean} useFurthestAway Set to true to take position furthest away
 * @return {HTMLDivElement[]} Markers where tooltip could not be placed
 */
function findTooltipPositions(markers1, markers2, doMarkerCheck, useFurthestAway) {
  const positions = ['top-center', 'left-top', 'right-top', 'left-center', 'left-bottom', 'right-center', 'bottom-center', 'right-bottom'];
  const tryAgainMarkers = [];

  for (const marker1 of markers1) {
    const newPositions = [];

    // Run through all positions to see if they can be used
    for (const position of positions) {
      let collision = false;
      const distances = [];
      // Check collisions with other points
      for (const marker2 of markers2) {
        if (marker1 !== marker2) {
          // Add new position to marker1
          marker1.classList.remove(...positions);
          marker1.classList.add(position);

          const checkResult = checkCollision(marker1, marker2, doMarkerCheck);
          if (checkResult.collision) {
            collision = true;
            break; // Vi har en kollision med imellem marker 1' nye postion og marker 2. Så bare stop her
          } else {
            distances.push(checkResult.distance);
          }
        }
      }

      // Hvis ingen kollision, så put position i array med de positioner som kan benyttes
      if (!collision) {
        newPositions.push({ position, distance: Math.min(...distances) });
      }
    }

    // Tag den første og bedste ny position og giv den til marker1
    marker1.classList.remove(...positions);

    if (newPositions.length > 0) {
      if (useFurthestAway) {
        marker1.classList.add(newPositions.sort((a, b) => b.distance - a.distance)[0]?.position); // Tag den position som er længst væk fra de andre tooltips
      } else {
        marker1.classList.add(newPositions[0]?.position);
      }
    } else {
      tryAgainMarkers.push(marker1);
    }
  }

  return tryAgainMarkers;
}

/**
 * Tries to find a good position for tooltip on the marker
 * @param {HTMLDivElement[]} markers
 * @param {boolean} useFurthestAway Set to true to take position furthest away
 */
function findOptimalTooltipPositions(markers, useFurthestAway) {
  const sortedMarkers = sortMarkers(markers).filter(m => !m.classList.contains('fixed-position'));
  const tryAgainMarkers = findTooltipPositions(sortedMarkers, sortedMarkers, true, useFurthestAway);

  if (tryAgainMarkers.length > 0) {
    const removeTheseMarkers = findTooltipPositions(tryAgainMarkers, sortedMarkers, false, useFurthestAway); // Try to position tooltip igain.

    // TODO: What to do
    removeTheseMarkers.forEach(m => m.style.opacity = '0.2');
  }
}

/**
 * Helper funktions to markers
 * @param {HTMLDivElement} map Map div that wraps the svg
 */
function markerHelper(map) {
  const markerClass = 'accommodation-frontpage-location__marker';

  const getAllMarkers = () => {
    return /** @type{HTMLDivElement[]} */ ([...map.querySelectorAll(`.${markerClass}`)]);
  };

  return {
    /**
     * @param {string} geographyId
     * @param {string} countryId
     * @returns {HTMLDivElement}
     */
    getMarkerById: (geographyId, countryId) => {
      geographyId = geographyId ?? 'null';
      countryId = countryId ?? 'null';
      return map.querySelector(`.${markerClass}[data-geography-id="${geographyId}"][data-country-id="${countryId}"]`);
    },

    /**
     * @returns {HTMLDivElement[]}
     */
    getAllMarkers,

    /**
     * Removes '.is-active' from all markers
     */
    setAllMarkersInactive: () => {
      getAllMarkers().forEach(m => m.classList.remove('is-active'));
    },

    /**
     * Create html div marker on map div
     * @param {string} activeCountryId
     * @param {string} title
     * @param {string} countryId
     * @param {string} geographyId
     * @param {{ x: number, y: number }} positionOnSvg
     * @param {string} [tooltipPosition]
     * @returns {HTMLDivElement}
     */
    createMarkerOnMap: (activeCountryId, title, countryId, geographyId, positionOnSvg, tooltipPosition) => {
      const marker = document.createElement('div');
      marker.classList.add(markerClass);

      if (tooltipPosition) {
        marker.classList.add(tooltipPosition, 'fixed-position');
      } else if (activeCountryId === 'all') {
        marker.classList.add('top-center');
      } else {
        marker.classList.add('right-center');
      }

      marker.dataset.title = title;
      marker.dataset.countryId = countryId;
      marker.dataset.geographyId = geographyId;
      marker.dataset.positionOnSvg = JSON.stringify(positionOnSvg);
      map.appendChild(marker);
      return marker;
    },

    /**
     * Get closest marker to element
     * @param {Element} element
     * @returns {HTMLDivElement}
     */
    getClosestMarker: (element) => {
      return /** @type{HTMLDivElement} */ (element.closest(`.${markerClass}`));
    },
  };
}

/**
 * Svgmap helpers to frontpagemap
 * @param {SVGElement} svg
 * @param {HTMLDivElement} map Map div that wraps the svg
 * @param {CountryTermModel[]} terms Terms
 */
export function svgMapHelper(svg, map, terms) {
  const { getAllMarkers, getMarkerById, setAllMarkersInactive, createMarkerOnMap, getClosestMarker } = markerHelper(map);

  /**
  */
  let onMarkerClickCallback;
  const onMarkerClick = (event) => {
    setAllMarkersInactive();

    const marker = getClosestMarker(event.target);
    marker.classList.add('is-active');
    onMarkerClickCallback(marker.dataset);
  };

  return {
    /**
     * Whenever a point is clicked, callback is called
     * @param {(arg0: MarkerClickPayload) => void} callback
     */
    onMarkerClick: (callback) => {
      onMarkerClickCallback = callback;
    },

    /**
     * @param {ViewBox} targetViewBox New viewBox to navigate to
     * @param {number} duration How long the animation should be. In miliseconds
     * @param {() => void} [doneCallback] When animation is done
     */
    animateViewBox: (targetViewBox, duration, doneCallback) => {
      let startTime = 0;
      const initialViewBox = svg.getAttribute('viewBox').split(' ').map(Number);

      /**
       * @param {number} time
       */
      function animate(time) {
        if (startTime === 0) {
          startTime = time;
        }

        const elapsed = time - startTime;
        const progress = Math.min(elapsed / duration, 1); // Clamp to 1

        // Apply effect
        const easedProgress = easeInOut(progress);

        // Calculate new viewBox values
        const newViewBox = initialViewBox.map((start, index) => {
          return start + (targetViewBox[index] - start) * easedProgress;
        });

        // Set the new viewBox
        svg.setAttribute('viewBox', newViewBox.join(' '));

        if (progress < 1) {
          requestAnimationFrame(animate);
          return;
        }

        if (doneCallback != null) {
          doneCallback();
        }
      }

      requestAnimationFrame(animate);
    },

    /**
     * Adds markers to the map.
     * It gets markers from visible circles with class .point in the svg
     * @param {string} activeCountryId The isocode like DE
     * @param {boolean} mapIsExpanded If map is visible do add markers
     */
    addMarkers: (activeCountryId, mapIsExpanded) => {
      if (!mapIsExpanded) {
        return;
      }

      /** @type TermModel[] */
      const countryTerms = terms.map(tt => ({ countryId: tt.countryId, name: tt.countryName, x: tt.countryPosition.x, y: tt.countryPosition.y, geographyId: null, countryIsoCode: tt.countryIsoCode }));

      /** @type TermModel[] */
      let t = activeCountryId === 'all'
        ? countryTerms
        : terms.find(d => d.countryIsoCode.toLowerCase() === activeCountryId.toLowerCase()).terms;

      // If no terms, then show the country dot/tooltip
      if (t.length === 0) {
        t = countryTerms.filter(d => d.countryIsoCode.toLowerCase() === activeCountryId.toLowerCase());
      }

      t.forEach((tt) => {
        const positionOnSvg = { x: tt.x, y: tt.y };
        const marker = createMarkerOnMap(activeCountryId, tt.name, tt.countryId, tt.geographyId, positionOnSvg, tt.tooltipPosition);
        setMarkerPosition(marker, positionOnSvg, svg, map.parentElement);
        marker.addEventListener('click', onMarkerClick);
      });

      const markers = getAllMarkers();

      findOptimalTooltipPositions(markers, activeCountryId !== 'all');

      markers.forEach(m => m.classList.add('is-ready'));
    },

    /**
     * Called when browser window changes. (Media query)
     */
    recalculateMarkerPositions: () => {
      const markers = getAllMarkers();
      markers.forEach((marker) => {
        setMarkerPosition(marker, JSON.parse(marker.dataset.positionOnSvg), svg, map.parentElement);
      });
    },

    /**
     * Removes all markers on the map. Also removes eventlisteners
     */
    removeMarkers: () => {
      const markers = getAllMarkers();
      markers.forEach((marker) => {
        marker.removeEventListener('click', onMarkerClick);
        removeElement(marker);
      });
    },

    /**
     * Sets all markers inactive and
     * sets marker active if it can find a marker with geographyId
     * @param {string} geographyId
     * @param {string} countryId
     */
    setMarkerActive: (geographyId, countryId) => {
      setAllMarkersInactive();

      const marker = getMarkerById(geographyId, countryId);
      if (marker != null) {
        marker.classList.add('is-active');
      }
    },
  };
};
